import React from "react";

/**
 * Shared utilities for detecting ICD-10-AM / ACHI style codes and code
 * ranges inside free-text fields (Includes, Excludes, Note, Codealso,
 * Stylednote, etc.) and rendering them as clickable hyperlinks.
 */

// A single code "core": letter + 2-4 digits, optional decimal part.
// Matches: R78, R78.0, Z14, C80.9, M9310 ...
const CODE_CORE = `(?:[A-Z][0-9]{2,4}(?:\\.[0-9]{1,2})?|[0-9]{4,6}-[0-9]{2})`;

// Range: two codes separated by a hyphen or an en/em dash, with optional
// wrapping parentheses around the whole thing.
// Matches: A00-B99, (C00-D48), F00–F99, Z14-Z16
const RANGE_REGEX = new RegExp(
  `\\(?${CODE_CORE}\\s*[-–—]\\s*${CODE_CORE}\\)?`,
  "g",
);

// Single code, optionally wrapped in parentheses, optionally followed by
// a dagger/asterisk/plus marker or a trailing hyphen.
// Matches: R78.0, (M9310/0)... (decimal-slash forms fall back to the
// letter+digits core), Z50.3-, A23.-†
const SINGLE_CODE_REGEX = new RegExp(
  `\\(?${CODE_CORE}(?:-[†*+]|[†*+]|-)?\\)?`,
  "g",
);
const ACHI_BLOCK_REGEX = /\b([0-9]{4,6})\s*\[([0-9]+)\]/g;
const GENERIC_SEE_TERMS = new Set([
  "condition",
  "condition, by type",
]);
/**
 * Splits `text` into an ordered list of segments:
 *   { type: 'text', text }
 *   { type: 'code' | 'range', text, start, end }
 *
 * Ranges are detected first; any single-code match that overlaps an
 * already-found range is discarded, so "A00-B99" isn't double-matched
 * as "A00" + "-B99".
 */
export function parseCodeSegments(text) {
  if (!text) return [];

  const achiBlocks = [];
  let match;

  ACHI_BLOCK_REGEX.lastIndex = 0;
  while ((match = ACHI_BLOCK_REGEX.exec(text)) !== null) {
    achiBlocks.push({
      type: "achi-block",
      text: match[0],
      start: match.index,
      end: match.index + match[0].length,
      codePrefix: match[1],
      blockNumber: match[2],
    });
  }

  const ranges = [];
  RANGE_REGEX.lastIndex = 0;
  while ((match = RANGE_REGEX.exec(text)) !== null) {
    const start = match.index;
    const end = start + match[0].length;
    const overlapsAchiBlock = achiBlocks.some((b) => start < b.end && end > b.start);
    if (!overlapsAchiBlock) {
      ranges.push({ type: "range", text: match[0], start, end });
    }
  }

  const singles = [];
  SINGLE_CODE_REGEX.lastIndex = 0;
  while ((match = SINGLE_CODE_REGEX.exec(text)) !== null) {
    const start = match.index;
    const end = start + match[0].length;
    const overlaps =
      ranges.some((r) => start < r.end && end > r.start) ||
      achiBlocks.some((b) => start < b.end && end > b.start);
    if (!overlaps) {
      singles.push({ type: "code", text: match[0], start, end });
    }
  }

  const tokens = [...achiBlocks, ...ranges, ...singles].sort(
    (a, b) => a.start - b.start,
  );

  const segments = [];
  let cursor = 0;
  for (const token of tokens) {
    if (token.start > cursor) {
      segments.push({ type: "text", text: text.slice(cursor, token.start) });
    }
    segments.push(token);
    cursor = token.end;
  }
  if (cursor < text.length) {
    segments.push({ type: "text", text: text.slice(cursor) });
  }

  return segments;
}

/**
 * Extracts the navigable code from a matched token. For a range, this is
 * the first (start) code — clicking a range jumps to the start of it in
 * Tabular, same as clicking a single code.
 */
export function cleanCodeToken(token) {
  const coreMatch = token.match(new RegExp(CODE_CORE));
  return coreMatch ? coreMatch[0] : null;
}

/**
 * Search-term highlighter, moved here so both the parser and the
 * consuming component share one implementation.
 */
export function highlightText(text, query, onChangeCodeBook = false) {
  if (!query || query.length < 2 || !text) {
    return text;
  }
  const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regex = onChangeCodeBook
    ? new RegExp(escapedQuery, "i")
    : new RegExp(`^${escapedQuery}$`);
  if (regex.test(text)) {
    return <span className="highlighted">{text}</span>;
  }
  return text;
}

/**
 * Renders `text` as a mix of plain (search-highlighted) spans and
 * clickable code/range spans.
 *
 * @param text            raw field text (Includes, Excludes, Note, etc.)
 * @param options.searchQuery       current search term, for highlighting
 * @param options.onChangeCodeBook  substring vs exact-match highlight mode
 * @param options.onCodeClick       fn(code: string) — navigate to Tabular
 * @param options.keyPrefix         unique prefix for React keys
 */
export function renderTextWithCodeLinks(
  text,
  {
    searchQuery,
    onChangeCodeBook,
    onCodeClick,
    onBlockNavigate, // 👈 new option
    keyPrefix = "seg",
  } = {},
) {
  if (!text) return null;

  const segments = parseCodeSegments(text);

  return segments.map((seg, i) => {
    const key = `${keyPrefix}-${i}`;

    if (seg.type === "text") {
      return (
        <span key={key}>
          {highlightText(seg.text, searchQuery, onChangeCodeBook)}
        </span>
      );
    }

    if (seg.type === "achi-block") {
      return (
        <span
          key={key}
          className="code-underline"
          onClick={(e) => {
            e.stopPropagation();
            onBlockNavigate?.(seg.codePrefix, seg.blockNumber);
          }}
          style={{ cursor: "pointer", marginRight: "2px" }}
          title={`Click to view code ${seg.codePrefix} in block ${seg.blockNumber}`}
        >
          {highlightText(seg.text, searchQuery, onChangeCodeBook)}
        </span>
      );
    }

    const navigateCode = cleanCodeToken(seg.text);

    return (
      <span
        key={key}
        className="code-underline"
        onClick={(e) => {
          e.stopPropagation();
          if (navigateCode) onCodeClick(navigateCode, seg.type);
        }}
        style={{ cursor: navigateCode ? "pointer" : "default", marginRight: "2px" }}
        title={
          seg.type === "range"
            ? "Click to view in Tabular"
            : "Click to search in Tabular"
        }
      >
        {highlightText(seg.text, searchQuery, onChangeCodeBook)}
      </span>
    );
  });
}
// ---- "See" / "See also" cross-reference hyperlinking (Index -> Index only) ----

const SEE_PREFIX_REGEX = /^\s*(see\s+also|see)\s+/i;

/**
 * Splits a "see"/"see also" field into its pieces, tolerating an optional
 * wrapping pair of parentheses around the whole reference.
 *   "see also Injury/superficial"    -> { open:"", prefix:"see also ", rest:"Injury/superficial", close:"" }
 *   "(see also Injury/superficial)"  -> { open:"(", prefix:"see also ", rest:"Injury/superficial", close:")" }
 */
export function splitSeePrefix(text = "") {
  let str = text;
  let open = "";
  let close = "";

  const wrapped = str.match(/^\s*\((.*)\)\s*$/);
  if (wrapped) {
    open = "(";
    close = ")";
    str = wrapped[1];
  }

  const match = str.match(SEE_PREFIX_REGEX);
  if (!match) return { open, prefix: "", rest: str, close };

  return {
    open,
    prefix: match[0],
    rest: str.slice(match[0].length),
    close,
  };
}

export function isGenericSeeReference(text) {
  if (!text) return true;
  const { rest } = splitSeePrefix(text);
  const normalized = rest.trim().toLowerCase().replace(/\s+/g, " ");
  return GENERIC_SEE_TERMS.has(normalized);
}

export function parseSeeReferencePath(text) {
  if (!text) return [];
  const { rest } = splitSeePrefix(text);
  return rest.split("/").map((s) => s.trim()).filter(Boolean);
}

export function renderSeeReference(
  text,
  { searchQuery, onChangeCodeBook, onNavigate, className } = {},
) {
  if (!text) return null;
  const cleaned = text.replace(/^\s*/, "");
  const { open, prefix, rest, close } = splitSeePrefix(cleaned);
  const term = rest.trim();

  if (isGenericSeeReference(cleaned) || !onNavigate) {
    return <>{highlightText(cleaned, searchQuery, onChangeCodeBook)}</>;
  }

  return (
    <>
      {open && <span>{open}</span>}
      {prefix && (
        <span>{highlightText(prefix, searchQuery, onChangeCodeBook)}</span>
      )}
      <span
        className={className || "see-reference-link"}
        onClick={(e) => {
          e.stopPropagation();
          onNavigate(term);
        }}
        style={{ cursor: "pointer" }}
        title="Click to view in Index"
      >
        {highlightText(term, searchQuery, onChangeCodeBook)}
      </span>
      {close && <span>{close}</span>}
    </>
  );
}